import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Flag,
  MapPin,
  Navigation,
  Play,
  Users,
  Loader2,
  Check,
  List,
  X,
} from "lucide-react";
import {
  getTripPlan,
  startTrip,
  setCommonPickup,
} from "../../services/carpoolTripService";

const GREEN = "#1A3C34";
const ORANGE = "#FF6B35";
const MAPS_KEY =
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY ||
  "AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8";

const POOL_NAME = {
  MORNING: "Morning pool",
  AFTERNOON: "Afternoon pool",
  EVENING: "Evening pool",
};

const coordStr = (s) => `${s.latitude},${s.longitude}`;

/**
 * Driver-facing group-trip screen, mirroring the mobile app:
 *  - planned: a "pickup sequence" timeline of every accepted rider with one
 *    "I've started driving" action that notifies everyone,
 *  - started: a live route map with a Rider list / Navigate toggle.
 * Rider state (picked up / next / waiting) comes from each stop's status.
 */
export default function GroupTripPanel({ date, timeWindow, onTripStarted, onClose }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [showPickupForm, setShowPickupForm] = useState(false);
  const [pickupLabel, setPickupLabel] = useState("");
  const [settingPickup, setSettingPickup] = useState(false);
  const [activeTab, setActiveTab] = useState("list");

  const loadPlan = useCallback(async () => {
    try {
      const data = await getTripPlan(date);
      setPlan(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load your trip");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const handleStart = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const result = await startTrip(date);
      toast.success(
        `${result.passenger_count} rider${result.passenger_count === 1 ? "" : "s"} can now see your live location.`
      );
      setPlan(result.plan ?? plan);
      setActiveTab("map");
      onTripStarted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trip");
    } finally {
      setStarting(false);
    }
  };

  const handleSetCommonPickup = () => {
    if (settingPickup) return;
    if (!pickupLabel.trim()) {
      toast.error('Name the meeting point (e.g. "Metro Station Gate 2")');
      return;
    }
    if (!navigator.geolocation) {
      toast.error("Geolocation is not available in this browser");
      return;
    }
    setSettingPickup(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await setCommonPickup(date, pickupLabel.trim(), pos.coords.latitude, pos.coords.longitude);
          toast.success("All passengers were notified to meet you there.");
          setShowPickupForm(false);
          setPickupLabel("");
          await loadPlan();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Could not set the common pickup point");
        } finally {
          setSettingPickup(false);
        }
      },
      () => {
        toast.error("Allow location access to drop a common pickup point.");
        setSettingPickup(false);
      },
      { enableHighAccuracy: true }
    );
  };

  const passengerCount = plan?.passenger_count ?? 0;
  const started = plan?.status === "started";
  const poolName = (timeWindow && POOL_NAME[timeWindow]) || "Your pool";
  const dest = plan?.destination || null;
  const destLabel = dest?.label || "your destination";

  // First stop not yet picked up is the one we're heading to.
  const nextIndex = plan?.route?.findIndex((s) => s.status !== "picked_up") ?? -1;
  const riderState = (stop, i) => {
    if (stop.status === "picked_up") return "picked_up";
    return i === nextIndex ? "next" : "waiting";
  };

  const openNavigation = (stop) => {
    const target = stop || plan?.route?.[0];
    const lat = target?.latitude ?? dest?.latitude;
    const lng = target?.longitude ?? dest?.longitude;
    if (lat == null || lng == null) {
      toast.error("This stop has no map location yet.");
      return;
    }
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`,
      "_blank"
    );
  };

  // ---- Header ----
  const Header = (
    <div
      className="flex items-center px-5 py-4 text-white"
      style={{ backgroundColor: GREEN }}
    >
      <div className="flex-1">
        <p className="text-lg font-extrabold leading-tight">{poolName}</p>
        <p className="text-sm text-white/70">
          {passengerCount} rider{passengerCount === 1 ? "" : "s"} · to {destLabel}
        </p>
      </div>
      {started ? (
        <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-bold">On the way</span>
      ) : (
        <button type="button" onClick={onClose} aria-label="Close" className="p-1">
          <X size={22} />
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div>
        {Header}
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: GREEN }} />
          <p className="mt-3 text-gray-500">Planning your route...</p>
        </div>
      </div>
    );
  }

  if (passengerCount === 0) {
    return (
      <div>
        {Header}
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Users className="h-10 w-10 text-gray-400" />
          <p className="mt-3 font-semibold text-gray-700">No riders yet</p>
          <p className="text-sm text-gray-400">Accept ride requests to plan your pool.</p>
        </div>
      </div>
    );
  }

  // ---- Rider row ----
  const Node = ({ state }) => {
    if (state === "picked_up") {
      return (
        <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-green-600 text-white">
          <Check size={14} />
        </span>
      );
    }
    if (state === "next") {
      return <span className="h-[18px] w-[18px] rounded-full" style={{ backgroundColor: ORANGE }} />;
    }
    return <span className="h-[18px] w-[18px] rounded-full border-2 border-gray-300 bg-white" />;
  };

  const RiderRow = (stop, i, compact) => {
    const state = riderState(stop, i);
    let sub = "waiting";
    if (state === "picked_up") sub = "picked up";
    else if (state === "next") sub = stop.eta_minutes != null ? `next stop · ${stop.eta_minutes} min away` : "next stop";
    return (
      <div key={stop.order} className="flex">
        <div className="flex w-7 flex-col items-center">
          <Node state={state} />
          <div className="my-1 w-0.5 flex-1 bg-gray-200" style={{ minHeight: 18 }} />
        </div>
        <div className="flex-1 pb-5 pl-3">
          <div className="flex items-center gap-1">
            <p className="font-bold text-gray-900">{stop.name || `Rider ${i + 1}`}</p>
            {state === "next" && (
              <span className="text-sm font-bold" style={{ color: ORANGE }}>· next stop</span>
            )}
          </div>
          <p className="text-sm text-gray-500">{sub}</p>
        </div>
        {!compact && state === "next" && (
          <button
            type="button"
            onClick={() => openNavigation(stop)}
            className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full"
            style={{ backgroundColor: "#FFF1E8" }}
            aria-label="Navigate"
          >
            <Navigation size={16} style={{ color: ORANGE }} />
          </button>
        )}
      </div>
    );
  };

  const RiderTimeline = (compact) => (
    <>
      {plan.route.map((stop, i) => RiderRow(stop, i, compact))}
      <div className="flex">
        <div className="flex w-7 flex-col items-center">
          <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-gray-800 text-white">
            <Flag size={12} />
          </span>
        </div>
        <div className="flex-1 pl-3">
          <p className="font-bold text-gray-900">{destLabel === "your destination" ? "Destination" : destLabel}</p>
          <p className="text-sm text-gray-500">
            Destination{plan.total_distance_km != null ? ` · ${plan.total_distance_km} km` : ""}
          </p>
        </div>
      </div>
    </>
  );

  // ---- PRE-START ----
  if (!started) {
    return (
      <div>
        {Header}
        <div className="px-5 py-5">
          <p className="mb-4 text-xs font-bold tracking-widest text-gray-400">PICKUP SEQUENCE</p>
          {RiderTimeline(false)}

          <div className="mb-4 mt-1 flex items-center gap-2 rounded-xl px-4 py-3" style={{ backgroundColor: "#FEF6E7" }}>
            <span style={{ color: "#92400E" }}>📣</span>
            <span className="text-sm font-semibold" style={{ color: "#92400E" }}>
              Update once — everyone gets notified
            </span>
          </div>

          <Button
            className="w-full py-6 text-base text-white"
            style={{ backgroundColor: ORANGE }}
            onClick={handleStart}
            disabled={starting}
          >
            {starting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <Play className="mr-2" size={20} />
                I've started driving
              </>
            )}
          </Button>
          <p className="mt-2 text-center text-xs text-gray-400">
            Riders see your live location after this
          </p>

          {!plan.common_pickup && !showPickupForm && (
            <Button
              variant="outline"
              className="mt-4 w-full"
              style={{ borderColor: GREEN, color: GREEN }}
              onClick={() => setShowPickupForm(true)}
            >
              <MapPin className="mr-2" size={16} />
              Use a common pickup point instead
            </Button>
          )}

          {showPickupForm && (
            <div className="mt-4">
              <p className="mb-2 text-xs text-gray-400">Drops a meeting point at your current location.</p>
              <Input
                placeholder='Label e.g. "Metro Gate 2"'
                value={pickupLabel}
                onChange={(e) => setPickupLabel(e.target.value)}
              />
              <div className="mt-3 flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => { setShowPickupForm(false); setPickupLabel(""); }}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 text-white"
                  style={{ backgroundColor: GREEN }}
                  onClick={handleSetCommonPickup}
                  disabled={settingPickup}
                >
                  {settingPickup ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set & Notify"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- STARTED: map + tabs ----
  const stopsWithCoords = plan.route.filter((s) => s.latitude != null && s.longitude != null);
  const destCoords = dest?.latitude != null ? { latitude: dest.latitude, longitude: dest.longitude } : null;
  const mapPoints = [...stopsWithCoords, ...(destCoords ? [destCoords] : [])];

  let mapSrc = null;
  if (mapPoints.length >= 2) {
    const origin = coordStr(mapPoints[0]);
    const destination = coordStr(mapPoints[mapPoints.length - 1]);
    const waypoints = mapPoints.slice(1, -1).map(coordStr).join("|");
    mapSrc =
      `https://www.google.com/maps/embed/v1/directions?key=${MAPS_KEY}` +
      `&origin=${origin}&destination=${destination}` +
      (waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : "") +
      `&mode=driving`;
  } else if (mapPoints.length === 1) {
    mapSrc = `https://www.google.com/maps/embed/v1/place?key=${MAPS_KEY}&q=${coordStr(mapPoints[0])}`;
  }

  return (
    <div>
      {Header}

      <div className="h-64 w-full bg-gray-100">
        {mapSrc ? (
          <iframe
            title="Trip route"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src={mapSrc}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">
            No route locations available
          </div>
        )}
      </div>

      <div className="flex items-center border-b border-gray-100">
        <button
          type="button"
          className="flex flex-1 items-center justify-center gap-2 py-3.5 font-semibold"
          style={{ color: activeTab === "list" ? GREEN : "#9CA3AF" }}
          onClick={() => setActiveTab("list")}
        >
          <List size={16} /> Rider list
        </button>
        <div className="h-6 w-px bg-gray-100" />
        <button
          type="button"
          className="flex flex-1 items-center justify-center gap-2 py-3.5 font-semibold"
          style={{ color: ORANGE }}
          onClick={() => { setActiveTab("map"); openNavigation(); }}
        >
          <Navigation size={16} /> Navigate
        </button>
      </div>

      <div className="px-5 py-4">
        {plan.route.map((stop, i) => RiderRow(stop, i, true))}
      </div>
    </div>
  );
}
